import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import RemoveIcon from "@mui/icons-material/Remove";
import * as MUI from "@mui/material";
import { useTranslation } from "react-i18next";
import { useWarehouseLabelBuffer } from "../../../../../../contexts/WarehouseLabelBuffer";
import { useDownloadWarehouseLabelsBatch } from "../../../../../../hooks/useWarehouseItemQrCode";
import { trimText } from "../../../../../../utils/Functions";
import { useState } from "react";
import {
  PRINTING_METHODS,
  getFormatsForMethod,
  formatLabel,
} from "../utils/labelFormats";

function PanelHeader({ count, onClear, onClose }) {
  const { t } = useTranslation();
  return (
    <MUI.Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ p: 2.5, borderBottom: 1, borderColor: "divider" }}
    >
      <MUI.Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
        {t("LabelPrintQueue")}
      </MUI.Typography>
      <MUI.Chip label={count} size="small" color="primary" data-testid="warehouse.label-queue.panel.count-chip" />
      {count > 0 && (
        <MUI.Button
          size="small"
          onClick={onClear}
          sx={{ textTransform: "none" }}
          data-testid="warehouse.label-queue.panel.clear-all-button"
        >
          {t("ClearAll")}
        </MUI.Button>
      )}
      <MUI.IconButton onClick={onClose} size="small" data-testid="warehouse.label-queue.panel.close-button">
        <CloseIcon fontSize="small" />
      </MUI.IconButton>
    </MUI.Stack>
  );
}

function PanelBody({ entries, setQuantity, remove }) {
  const { t } = useTranslation();
  return (
    <MUI.Box sx={{ flexGrow: 1, overflowY: "auto", p: entries.length ? 1 : 2.5 }}>
      {entries.length === 0 ? (
        <MUI.Stack alignItems="center" spacing={1.5} sx={{ py: 6, color: "text.secondary" }}>
          <Inventory2OutlinedIcon fontSize="large" />
          <MUI.Typography variant="body2" align="center">
            {t("CheckItemsToPrintInfo")}
          </MUI.Typography>
        </MUI.Stack>
      ) : (
        <MUI.Stack spacing={1}>
          {entries.map(({ item, quantity }) => (
            <MUI.Paper
              key={item.id}
              variant="outlined"
              sx={{ p: 1.5, borderRadius: 2, display: "flex", alignItems: "center", gap: 1.5 }}
              data-testid={`warehouse.label-queue.panel.item.${item.id}`}
            >
              <MUI.Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <MUI.Tooltip title={item.title}>
                  <MUI.Typography
                    variant="body2"
                    sx={{ fontWeight: 600 }}
                    noWrap
                    data-testid={`warehouse.label-queue.panel.item.${item.id}.title`}
                  >
                    {trimText(item.title, 28)}
                  </MUI.Typography>
                </MUI.Tooltip>
                {item.code && (
                  <MUI.Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: "monospace" }}
                    data-testid={`warehouse.label-queue.panel.item.${item.id}.code`}
                  >
                    {item.code}
                  </MUI.Typography>
                )}
              </MUI.Box>

              <MUI.Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 0.25, flexShrink: 0 }}
              >
                <MUI.IconButton
                  size="small"
                  onClick={() => setQuantity(item.id, quantity - 1)}
                  data-testid={`warehouse.label-queue.panel.item.${item.id}.decrement-button`}
                >
                  <RemoveIcon fontSize="small" />
                </MUI.IconButton>
                <MUI.Typography
                  variant="body2"
                  sx={{ minWidth: 20, textAlign: "center" }}
                  data-testid={`warehouse.label-queue.panel.item.${item.id}.quantity`}
                >
                  {quantity}
                </MUI.Typography>
                <MUI.IconButton
                  size="small"
                  onClick={() => setQuantity(item.id, quantity + 1)}
                  data-testid={`warehouse.label-queue.panel.item.${item.id}.increment-button`}
                >
                  <AddIcon fontSize="small" />
                </MUI.IconButton>
              </MUI.Stack>

              <MUI.IconButton
                size="small"
                onClick={() => remove(item.id)}
                color="error"
                data-testid={`warehouse.label-queue.panel.item.${item.id}.delete-button`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </MUI.IconButton>
            </MUI.Paper>
          ))}
        </MUI.Stack>
      )}
    </MUI.Box>
  );
}

function PanelFooter({
  error,
  printingMethod,
  onMethodChange,
  formatId,
  onFormatChange,
  formatOptions,
  disabled,
  isLoading,
  onGenerate,
}) {
  const { t } = useTranslation();
  return (
    <MUI.Stack spacing={1.5} sx={{ p: 2.5, borderTop: 1, borderColor: "divider" }}>
      {error && (
        <MUI.Alert
          severity="error"
          sx={{ borderRadius: 2 }}
          data-testid="warehouse.label-queue.panel.error-alert"
        >
          {t("MissingLabelCaptions")}
        </MUI.Alert>
      )}
      <MUI.FormControl size="small" fullWidth>
        <MUI.InputLabel id="printing-method-label">{t("PrintingMethod")}</MUI.InputLabel>
        <MUI.Select
          labelId="printing-method-label"
          label={t("PrintingMethod")}
          value={printingMethod}
          onChange={(e) => onMethodChange(e.target.value)}
          SelectDisplayProps={{ "data-testid": "warehouse.label-queue.panel.method-select" }}
          inputProps={{ "data-testid": "warehouse.label-queue.panel.method-select.value" }}
        >
          <MUI.MenuItem
            value={PRINTING_METHODS.SHEET}
            data-testid={`warehouse.label-queue.panel.method-select.option.${PRINTING_METHODS.SHEET}`}
          >
            {t("SheetPrinting")}
          </MUI.MenuItem>
          <MUI.MenuItem
            value={PRINTING_METHODS.ROLL}
            data-testid={`warehouse.label-queue.panel.method-select.option.${PRINTING_METHODS.ROLL}`}
          >
            {t("RollPrinting")}
          </MUI.MenuItem>
        </MUI.Select>
      </MUI.FormControl>
      <MUI.FormControl size="small" fullWidth>
        <MUI.InputLabel id="label-format-label">{t("LabelFormat")}</MUI.InputLabel>
        <MUI.Select
          labelId="label-format-label"
          label={t("LabelFormat")}
          value={formatId}
          onChange={(e) => onFormatChange(e.target.value)}
          SelectDisplayProps={{ "data-testid": "warehouse.label-queue.panel.format-select" }}
          inputProps={{ "data-testid": "warehouse.label-queue.panel.format-select.value" }}
        >
          {formatOptions.map((format) => (
            <MUI.MenuItem
              key={format.id}
              value={format.id}
              data-testid={`warehouse.label-queue.panel.format-select.option.${format.id}`}
            >
              {formatLabel(format, t)}
            </MUI.MenuItem>
          ))}
        </MUI.Select>
      </MUI.FormControl>
      <MUI.Button
        variant="contained"
        disableElevation
        disabled={disabled || isLoading}
        onClick={onGenerate}
        startIcon={isLoading ? <MUI.CircularProgress size={18} color="inherit" /> : null}
        sx={{ borderRadius: 2 }}
        data-testid="warehouse.label-queue.panel.generate-button"
      >
        {t("GenerateLabelsPdf")}
      </MUI.Button>
    </MUI.Stack>
  );
}

export default function WarehouseLabelQueuePanel({ open, onClose }) {
  const { t } = useTranslation();
  const { entries, setQuantity, remove, clear } = useWarehouseLabelBuffer();
  const { downloadBatch, isLoading, error } = useDownloadWarehouseLabelsBatch();
  const [printingMethod, setPrintingMethod] = useState(PRINTING_METHODS.SHEET);
  const formatOptions = getFormatsForMethod(printingMethod);
  const [formatId, setFormatId] = useState(formatOptions[0].id);

  const handleMethodChange = (method) => {
    setPrintingMethod(method);
    setFormatId(getFormatsForMethod(method)[0].id);
  };

  const handleGenerate = () => {
    downloadBatch(
      entries.map(({ item, quantity }) => ({ itemId: item.id, quantity })),
      formatId,
      {
        nameLabel: t("Title"),
        priceLabel: t("Price"),
        codeLabel: t("Code"),
      },
    );
  };

  return (
    <MUI.Drawer anchor="right" open={open} onClose={onClose}>
      <MUI.Box
        sx={{ width: { xs: "100vw", sm: 420 }, display: "flex", flexDirection: "column", height: "100%" }}
        data-testid="warehouse.label-queue.panel"
      >
        <PanelHeader count={entries.length} onClear={clear} onClose={onClose} />
        <PanelBody entries={entries} setQuantity={setQuantity} remove={remove} />
        <PanelFooter
          error={error}
          printingMethod={printingMethod}
          onMethodChange={handleMethodChange}
          formatId={formatId}
          onFormatChange={setFormatId}
          formatOptions={formatOptions}
          disabled={entries.length === 0}
          isLoading={isLoading}
          onGenerate={handleGenerate}
        />
      </MUI.Box>
    </MUI.Drawer>
  );
}
